import type { MapiStory } from '@storyblok/openapi'
import dayjs from 'dayjs'
// we need to import the browser Sqrl package because the other includes node.js code
// see https://github.com/squirrellyjs/squirrelly/issues/240
import * as Sqrl from 'squirrelly/dist/browser/squirrelly.min.js'
import { parseLegacyTemplate } from './squirrelyHelpers'

type ExtendedTemplateContext = {
  fallbackSlugs: boolean
  prependString: string
  it: TemplateContext
}

Sqrl.helpers.define(
  'activeTranslatedSlug',
  ({ params }: { params: [ExtendedTemplateContext] }): string => {
    const [context] = params
    const { it, fallbackSlugs = true, prependString = '/' } = context
    const { story, lang, folder } = it
    const target = folder || story
    if (!lang) {
      return target.slug
    }

    const findSlug = (lang: string) =>
      target?.translated_slugs?.find((t) => t.lang === lang)

    let translatedSlug = findSlug(lang)

    if (typeof translatedSlug === 'undefined' && fallbackSlugs) {
      const splittedLang = lang.split('-')[0]
      if (splittedLang) {
        translatedSlug = findSlug(splittedLang)
      }
    }

    if (translatedSlug) {
      return prependString + translatedSlug.slug
    }

    return prependString + target.slug
  },
)

Sqrl.helpers.define(
  'formatDate',
  ({ params }: { params: [string, string, number?] }): string => {
    const [dateStr, format, utcOffset = 0] = params
    if (!dateStr) {
      return ''
    }

    return dayjs(dateStr).utcOffset(utcOffset).format(format)
  },
)

export type TemplateContext = {
  story: MapiStory
  lang: string
  folder?: Pick<
    MapiStory['breadcrumbs'][number],
    'slug' | 'translated_slugs'
  > & {
    translated_slug: string | undefined | null
  }
  branch_id?: number
  env_location?: string
  env_name?: string
}

function processTemplate(template: string, context: TemplateContext): string {
  const unescapedTemplate = template
    .replaceAll('&gt;', '>')
    .replaceAll('&lt;', '<')
  return Sqrl.render(unescapedTemplate, context)
}

export const getAdvancedPath = (path: string, ctx: TemplateContext): string => {
  let slug = ''

  // Build slug from breadcrumbs
  ctx.story.breadcrumbs?.forEach((bc) => {
    if (bc.path) {
      const translatedSlugs = bc.translated_slugs.filter(
        (translatedSlug): boolean => {
          return ctx.lang === translatedSlug.lang
        },
      )

      const context: TemplateContext = {
        ...ctx,
        ...{
          folder: {
            ...bc,
            translated_slug:
              translatedSlugs.length > 0 ? translatedSlugs[0]?.slug : bc.slug,
          },
        },
      }

      const breadcrumbsTemplate = parseLegacyTemplate(bc.path, context)
      slug += processTemplate(breadcrumbsTemplate, context)
    }
  })

  // Handle current story
  if (!!ctx.story.path && ctx.story.path !== '') {
    const renderTemplate = parseLegacyTemplate(ctx.story.path, ctx)
    slug += processTemplate(renderTemplate, ctx)
  } else if (ctx.story.is_startpage) {
    if (slug[slug.length - 1] === '/') {
      slug = slug.slice(0, -1)
    }
  } else {
    slug += ctx.story.slug
  }

  // Return absolute URL as-is, otherwise combine with base path
  if (slug.startsWith('http://') || slug.startsWith('https://')) {
    return '' + slug
  } else {
    return path + (slug === '/' ? '' : slug)
  }
}
